import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import {
  faArrowUp, faCheck, faHourglass, faTachometer, faWrench,
} from '@fortawesome/pro-light-svg-icons'
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import type { UseFiltersPayload } from '@domain-group/react-hooks'

import useAgencySelector from '@hooks/useAgencySelector'
import useCurrentUser from '@hooks/useCurrentUser'
import useSelector from '@hooks/useSelector'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PageContext from '@contexts/pageContext'

import defaultRequestOptions from '@sections/Client/packs/Overviews/defaultRequestOptions'

import useSignboards from '../../hooks/useSignboards'

import SignboardListItem from '../SignboardListItem'
import SignboardsBlankState from '../SignboardsBlankState'

const buildPageFilterFields = () => {
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        label: 'Filter by Address',
        entityKey: 'campaigns',
      },
      key: 'campaign_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'users',
        entityTitleKey: 'full_name',
        label: 'Agent',
        searchRequestOptions: {
          actable_types: ['Agent'],
        },
      },
      key: 'userId',
    },
    {
      callbacks: {
        buildSearchRequestOptions: () => ({ actable_types: ['OfficeAdministrator'] }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        label: 'Priority For',
        entityKey: 'users',
        entityTitleKey: 'full_name',
      },
      key: 'priority_order_items_by_user_id',
    },
  ]
}

interface PageContentProps {
  filterPayload: UseFiltersPayload,
}

const PageContent = (props: PageContentProps) => {
  const { filterPayload } = props
  const { pageFilters, persistedFilters } = filterPayload

  // @ts-expect-error TS2339
  const { priority_order_items_by_user_id: priorityQuotesUserId, userId } = pageFilters || {}

  const { currentUser, isAgent, isEbuAdmin } = useCurrentUser()
  const selectedPriorityUserId = priorityQuotesUserId || currentUser.id

  const users = useSelector(reduxState => reduxState.entities.users)

  const { selectedAgencyId } = useAgencySelector()

  useLocation()

  // @ts-expect-error TS2339
  const { status }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['status'])
  const currentStatus = status || 'all'

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters, updateFilters,
    },
    defaultFilters,
  } = pageContext

  const signboardsPayload = useSignboards({
    filters: {
      agency_id: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
      agent_id: userId ? users[userId]?.actable_id : undefined,
      campaignIsHidden: false,
      exclude_without_campaign: true,
      status,
      taskable_only: true,
      sort_by_priority_campaign_for_user_id: !priorityQuotesUserId ? currentUser.id : undefined,
      order_by: 'updated_at desc',
      ...pageFilters,
    },
    requestOptions: {
      ...defaultRequestOptions.orderItem,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderItemsCount,
    groupedOrderItems,
    hasFilteredOrderItems,
    loading,
  } = signboardsPayload

  const { agents } = useSelector(reduxState => reduxState.entities)

  return (
    <>
      <Helmet>
        <title>Signboards | Realhub</title>
      </Helmet>

      <PageHeader
        nestedNavigation
        activeTabBarItemKey={currentStatus}
        tabBarItems={[
          {
            icon: faTachometer,
            key: 'all',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: '' }),
            title: 'All Signboards',
          },
          {
            icon: faHourglass,
            key: 'pending',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'pending' }),
            title: 'Pending',
          },
          {
            icon: faCheck,
            key: 'approved',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'approved' }),
            title: 'Approved',
          },
          {
            icon: faArrowUp,
            key: 'installed',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'installed' }),
            title: 'Installed',
          },
          {
            icon: faWrench,
            key: 'processing',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'processing' }),
            title: 'Processing',
          },
          {
            icon: faArrowDown,
            key: 'removed',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'removed' }),
            title: 'Removed',
          },
        ]}
        title="Signboards"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation
              locationKey="agency.agencyDashboard.signboards.banner"
            />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredOrderItems && <SignboardsBlankState />}

            {!loading && hasFilteredOrderItems && Object.values(groupedOrderItems).map((datePeriod) => {
              if (datePeriod.orderItems.length){
                return (
                  <DashboardModule
                    contentBoxProps={{ flexDirection: 'column' }}
                    key={datePeriod.key}
                    title={datePeriod.title}
                  >
                    {datePeriod.orderItems.map(
                      orderItem => (
                        <SignboardListItem
                          agents={agents}
                          isPriorty={selectedPriorityUserId
                            && orderItem.priority_campaign_user_ids?.includes(selectedPriorityUserId)}
                          key={orderItem.id}
                          orderItem={orderItem}
                          // @ts-expect-error TS2322
                          selectedPriorityUserId={selectedPriorityUserId}
                        />
                      ),
                    )}
                  </DashboardModule>
                )
              }

              return null
            })}

            {!loading && canLoadMore && (
              <Box variant="white">
                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredOrderItemsCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </Box>
            )}

          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation
              locationKey="agency.agencyDashboard.signboards.sidebar"
            />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={persistedFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
